import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useApp } from "@/contexts/app-context";
import { ToolCallMessagePartComponent, useThreadRuntime } from "@assistant-ui/react";
import { ChevronDown, ChevronUp, Loader2, Play, Search } from "lucide-react";
import { useEffect, useState } from "react";

// Helper to safely parse JSON strings if the result comes as a string
const tryParseJSON = (str: string) => {
    try {
        return JSON.parse(str);
    } catch (e) {
        return null;
    }
};

export const InferTemplateTool: ToolCallMessagePartComponent = ({
    argsText,
    result,
}) => {
    const threadRuntime = useThreadRuntime();
    const [isCollapsed, setIsCollapsed] = useState(false);

    // Parse result
    const rawData = typeof result === 'string' ? tryParseJSON(result) : result;
    const isLoading = result === undefined;

    // Support structured output where data is nested
    const payloadData = rawData?.data || rawData;

    const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>();
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>();
    const [categories, setCategories] = useState<any[]>([]);
    const [templates, setTemplates] = useState<any[]>([]);
    const [isFetchingData, setIsFetchingData] = useState(true);
    const { projectId } = useApp();

    useEffect(() => {
        let isMounted = true;

        async function loadData() {

            if (!projectId) {
                if (isMounted) setIsFetchingData(false);
                return;
            }

            try {
                // We need to import these at the top of the file!
                // Wait, I should make sure getTemplateCategories and getTemplates are available. 
                // Since this tool edits the file, I'll add the imports in another replace_file_content call if needed.
                // Assuming they will be imported or I'll do a multi_replace instead. Let me just use them here.
                const { getTemplateCategories, getTemplates } = await import('@/services/supabase/templates');

                setIsFetchingData(true);
                const [cats, tmpls] = await Promise.all([
                    getTemplateCategories(Number(projectId)),
                    getTemplates(Number(projectId))
                ]);

                if (isMounted) {
                    setCategories(cats);
                    setTemplates(tmpls);
                }
            } catch (error) {
                console.error("Failed to fetch templates:", error);
            } finally {
                if (isMounted) setIsFetchingData(false);
            }
        }

        if (payloadData) {
            loadData();
        }
        return () => { isMounted = false; };
    }, [projectId, payloadData]);

    useEffect(() => {
        if (payloadData?.inferredCategoryId) {
            setSelectedCategoryId(String(payloadData.inferredCategoryId));
        }
        if (payloadData?.inferredTemplateId) {
            setSelectedTemplateId(String(payloadData.inferredTemplateId));
        }
    }, [payloadData]);

    if (isLoading || isFetchingData) {
        return (
            <Card className="w-full my-4 border-primary/20 bg-card/50">
                <CardContent className="flex items-center gap-4 p-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                        <Loader2 className="h-5 w-5 text-primary animate-spin" />
                    </div>
                    <div className="space-y-1">
                        <p className="font-medium text-sm">Thinking...</p>
                        <p className="text-xs text-muted-foreground">Inferring template and category based on your request...</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!payloadData) return null;

    const query = payloadData.query || "";
    const name = payloadData.name || "Unknown Entity";
    const description = payloadData.description || "";

    // Filter templates based on selected category if desired, but here we just show all
    // and let the user pick. If they pick a template, we could optionally auto-select its category.
    const filteredTemplates = selectedCategoryId && selectedCategoryId !== "all"
        ? templates.filter((t: any) => {
            const cat = categories.find((c: any) => String(c.id) === String(selectedCategoryId));
            return String(t.category_id || t.category) === String(selectedCategoryId) || t.category === cat?.name;
        })
        : templates;


    const handleProceed = () => {
        if (!selectedTemplateId) return;

        // Ensure category matches if not strictly set
        const finalCategoryId = selectedCategoryId ||
            templates.find((t: any) => String(t.id) === String(selectedTemplateId))?.category_id ||
            "1";

        // Send a message back to the AI
        threadRuntime.append({
            role: "user",
            content: [{ type: "text", text: `Template ID is ${selectedTemplateId}. Category ID is ${finalCategoryId}. Please proceed with creating the entity for: "${name}".\n\nDescription: ${description}` }]
        });
        setIsCollapsed(true);
    };

    return (
        <Card className="w-full my-4 overflow-hidden border">
            <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors bg-muted/30"
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Search className="h-4 w-4" />
                </div>
                <div className="flex-grow">
                    <p className="font-semibold text-sm">
                        Entity Template Selection
                    </p>
                    <p className="text-xs text-muted-foreground">
                        Review inferred template for '{query}'
                    </p>
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                    {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                </Button>
            </div>

            {!isCollapsed && (
                <div className="border-t">
                    <div className="p-4 space-y-4">
                        <p className="text-sm text-foreground">
                            I'm getting ready to draft <strong>{name}</strong>. Please confirm the category and template you'd like me to use.
                        </p>

                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="grid gap-2">
                                <label className="text-xs font-medium text-muted-foreground ml-1">Category</label>
                                <Select
                                    value={selectedCategoryId}
                                    onValueChange={setSelectedCategoryId}
                                >
                                    <SelectTrigger className="bg-background/50 text-sm h-9">
                                        <SelectValue placeholder="Select a category..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {categories.map((c: any) => (
                                            <SelectItem key={c.id} value={String(c.id)}>
                                                {c.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid gap-2">
                                <label className="text-xs font-medium text-muted-foreground ml-1">Template</label>
                                <Select
                                    value={selectedTemplateId}
                                    onValueChange={setSelectedTemplateId}
                                >
                                    <SelectTrigger className="bg-background/50 text-sm h-9">
                                        <SelectValue placeholder="Select a template..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {(filteredTemplates.length > 0 ? filteredTemplates : templates).map((t: any) => (
                                            <SelectItem key={t.id} value={String(t.id)}>
                                                {t.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 bg-muted/20 px-4 py-3 border-t">
                        <Button variant="ghost" size="sm" onClick={() => setIsCollapsed(true)}>
                            Dismiss
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleProceed}
                            className="bg-primary hover:bg-primary/90 text-white"
                            disabled={!selectedTemplateId}
                        >
                            <Play className="mr-2 h-4 w-4 fill-current" />
                            Proceed with AI
                        </Button>
                    </div>
                </div>
            )}
        </Card>
    );
};
